/**
 * Persists a provisional Group Watch knowledge-session join code per org/course/module
 * so instructors see the same value in attendance before the watch starts and across UI surfaces.
 * Overwritten when the server returns the canonical code after `create-group-watch-knowledge-session`.
 */

/** Instructor + learner UI: mode label for join-code knowledge checks (aligns with learner “Group Watch Sessions”). */
export const GROUP_WATCH_SESSION_MODE_LABEL = 'Group Watch Session'

const STORAGE_PREFIX = 'gw_kc_preview_code_v1'

function storageKey (organizationId, courseId, moduleId) {
  return `${STORAGE_PREFIX}:${organizationId}:${courseId}:${moduleId}`
}

function generateCode () {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let s = ''
  for (let i = 0; i < 6; i++) {
    s += chars[Math.floor(Math.random() * chars.length)]
  }
  return s
}

/**
 * New random join-code candidate for the current instructor flow (not persisted — pair with Pinia state).
 * @returns {string}
 */
export function generateGroupWatchKnowledgeSessionCode () {
  return generateCode()
}

/**
 * Reject values that are clearly not join codes (e.g. HTTP status leaked into `code`).
 * @param {string|number|null|undefined} raw
 * @returns {boolean}
 */
export function isPlausibleJoinCodeString (raw) {
  const s = raw != null ? String(raw).trim().toUpperCase() : ''
  if (!s || s.length < 4 || s.length > 16) {
    return false
  }
  if (/^\d{3}$/.test(s)) {
    return false
  }
  return /^[A-Z0-9]+$/.test(s)
}

/**
 * Join code stored on a group watch record (history / resume). Backend field names vary.
 * @param {object|null|undefined} record
 * @returns {string} uppercase code or ''
 */
export function joinCodeFromGroupWatchRecord (record) {
  if (!record || typeof record !== 'object') {
    return ''
  }
  let raw =
    record.groupWatchSessionCode ??
    record.group_watch_session_code ??
    record.sessionCode ??
    record.session_code ??
    record.knowledgeSessionCode ??
    record.knowledge_session_code ??
    record.groupWatchKnowledgeSessionCode ??
    record.group_watch_knowledge_session_code ??
    record.joinCode ??
    record.join_code ??
    record.learnerJoinCode ??
    record.learner_join_code ??
    null
  if ((raw == null || String(raw).trim() === '') && record.code != null && isPlausibleJoinCodeString(record.code)) {
    raw = record.code
  }
  if (raw == null || String(raw).trim() === '') {
    return ''
  }
  const out = String(raw).trim().toUpperCase()
  return isPlausibleJoinCodeString(out) ? out : ''
}

/**
 * @param {string|number|null|undefined} organizationId
 * @param {string|number|null|undefined} courseId
 * @param {string|number|null|undefined} moduleId
 * @returns {string} uppercase code
 */
export function getOrCreateGroupWatchKnowledgePreviewCode (organizationId, courseId, moduleId) {
  const org = organizationId != null ? String(organizationId) : ''
  const course = courseId != null ? String(courseId) : ''
  const mod = moduleId != null ? String(moduleId) : ''
  if (!org || !course || !mod) {
    return ''
  }
  const key = storageKey(org, course, mod)
  try {
    const raw = localStorage.getItem(key)
    if (raw) {
      const j = JSON.parse(raw)
      if (j && typeof j.code === 'string') {
        const c = j.code.trim().toUpperCase()
        if (c.length >= 4) {
          return c
        }
      }
    }
  } catch {
    /* ignore */
  }
  const code = generateCode()
  try {
    localStorage.setItem(key, JSON.stringify({ code, createdAt: Date.now() }))
  } catch {
    /* private mode / quota */
  }
  return code
}

export function setGroupWatchKnowledgePreviewCode (organizationId, courseId, moduleId, code) {
  const org = organizationId != null ? String(organizationId) : ''
  const course = courseId != null ? String(courseId) : ''
  const mod = moduleId != null ? String(moduleId) : ''
  const c = code != null ? String(code).trim().toUpperCase() : ''
  if (!org || !course || !mod || !c) {
    return
  }
  try {
    localStorage.setItem(storageKey(org, course, mod), JSON.stringify({ code: c, createdAt: Date.now() }))
  } catch {
    /* ignore */
  }
}

export function clearGroupWatchKnowledgePreviewCode (organizationId, courseId, moduleId) {
  const org = organizationId != null ? String(organizationId) : ''
  const course = courseId != null ? String(courseId) : ''
  const mod = moduleId != null ? String(moduleId) : ''
  if (!org || !course || !mod) {
    return
  }
  try {
    localStorage.removeItem(storageKey(org, course, mod))
  } catch {
    /* ignore */
  }
}
